import { useEffect, useState } from "react";
import { Settings as SettingsIcon, User, Users, Monitor, LogOut, Loader2, FileClock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TeamPage from "@/pages/Team";
import AuditLogTab from "@/pages/AuditLogTab";
import { PageHeader, PrimaryButton } from "@/components/design-system";

/* ============================================================================
   SİSTEM > AYARLAR
   ----------------------------------------------------------------------------
   MVP odaklı sadeleştirme (kullanıcı kararı 2026-02-25):
   • Şirket Bilgileri sekmesi kaldırıldı — fatura bilgileri lisans sürecinin
     bir parçası olarak sales-led kanalda toplanacak.
   • Profil (+ güvenlik alt-bölümü), Ekip ve Log (owner-only) sekmeleri.
============================================================================ */
export default function Settings() {
    const { t } = useI18n();
    const { session } = useAuth();
    const isOwner = session?.user?.role === "owner";
    const [active, setActive] = useState("profile");

    const tabs = [
        { id: "profile", label: t("settings.tabs.profile"), icon: User },
        { id: "team",    label: t("settings.tabs.team"),    icon: Users },
        // Log sekmesi: güvenlik gereği sadece hesap sahibi (owner) görebilir.
        // Üyeler sekmeyi hiç görmez, backend de 403 döner (require_owner).
        ...(isOwner ? [{ id: "log", label: "Log", icon: FileClock }] : []),
    ];

    return (
        <div className="space-y-6" data-testid="settings-page">
            <PageHeader
                icon={SettingsIcon}
                title={t("settings.title")}
                description={t("settings.subtitle")}
            />

            <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6" data-testid="settings-tabs">
                <nav className="space-y-0.5 self-start lg:sticky lg:top-20">
                    {tabs.map((tb) => (
                        <button
                            key={tb.id}
                            onClick={() => setActive(tb.id)}
                            data-testid={`settings-tab-${tb.id}`}
                            className={`w-full flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors ${
                                active === tb.id ? "bg-muted text-foreground font-medium" : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                            }`}
                        >
                            <tb.icon className="h-4 w-4" strokeWidth={1.5} />
                            <span>{tb.label}</span>
                        </button>
                    ))}
                </nav>

                <div>
                    {active === "profile" && <ProfileTab />}
                    {active === "team"    && <TeamPage embedded />}
                    {active === "log"     && isOwner && <AuditLogTab />}
                </div>
            </div>
        </div>
    );
}

/* ============================================================================
   Shared blocks
============================================================================ */
const Section = ({ title, desc, children, action }) => (
    <div className="rounded-xl border border-border bg-surface">
        <div className="px-5 pt-5 pb-3 border-b border-border flex items-start justify-between gap-3">
            <div>
                <h3 className="font-display text-base font-semibold tracking-tight">{title}</h3>
                {desc && <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>}
            </div>
            {action}
        </div>
        <div className="p-5">{children}</div>
    </div>
);

const Field = ({ label, children, hint }) => (
    <label className="block space-y-1.5">
        <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">{label}</span>
        {children}
        {hint && <span className="block text-[11px] text-muted-foreground">{hint}</span>}
    </label>
);

const initials = (name = "") =>
    name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]).join("").toUpperCase() || "?";

/* ============================================================================
   Profile tab — backend-driven
============================================================================ */
const ProfileTab = () => {
    const { t } = useI18n();
    const { session, refresh } = useAuth();
    const user = session?.user;
    const tenant = session?.tenant;
    const [name, setName] = useState(user?.name || "");
    const [saving, setSaving] = useState(false);
    const dirty = (user?.name || "") !== name.trim();

    useEffect(() => { setName(user?.name || ""); }, [user?.name]);

    const save = async () => {
        const v = name.trim();
        if (!v) return;
        setSaving(true);
        try {
            await api.patch("/auth/me", { name: v });
            await refresh();
            toast.success(t("settings.profile.saved"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Kaydedilemedi");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="profile-tab">
            <Section title={t("settings.profile.title")} desc={t("settings.profile.desc")}>
                <div className="flex items-center gap-4 mb-6">
                    <Avatar className="h-14 w-14 border border-border">
                        <AvatarFallback className="bg-foreground text-background font-display font-bold">
                            {initials(user?.name)}
                        </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                        <div className="font-display text-base font-semibold">{user?.name}</div>
                        <div className="text-[11px] text-muted-foreground font-mono">{user?.email}</div>
                    </div>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                        {user?.role === "owner" ? t("role.owner") : t("role.member")}
                    </Badge>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label={t("settings.profile.name")}>
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            maxLength={80}
                            className="h-9 text-sm"
                            data-testid="profile-name-input"
                        />
                    </Field>
                    <Field label={t("settings.profile.email")}>
                        <Input value={user?.email || ""} className="h-9 text-sm" disabled />
                    </Field>
                    <Field label={t("settings.profile.company")}>
                        <Input value={tenant?.company_name || ""} className="h-9 text-sm" disabled />
                    </Field>
                    <Field label={t("settings.profile.role")}>
                        <Input value={user?.role === "owner" ? t("role.owner") : t("role.member")} className="h-9 text-sm" disabled />
                    </Field>
                </div>

                <div className="mt-6 flex justify-end">
                    <PrimaryButton
                        onClick={save}
                        disabled={!dirty || saving}
                        data-testid="profile-save-btn"
                    >
                        {saving ? "Kaydediliyor…" : t("settings.profile.save")}
                    </PrimaryButton>
                </div>
            </Section>

            <SecuritySection />
        </div>
    );
};

/* ============================================================================
   Security section — Aktif oturum + tüm oturumları kapat
   ----------------------------------------------------------------------------
   Full multi-session listing would need a `user_sessions` collection with
   refresh-token rotation, which is a much larger scope. Bu MVP, `token_rev`
   pattern'i kullanıyor: revoke-all `users.token_rev`'i bump'lıyor, tüm
   outstanding JWT'ler anında geçersizleşiyor (backend `get_tenant_context`
   içinde rev karşılaştırılıyor).
============================================================================ */
const SecuritySection = () => {
    const [session, setSession] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmRevoke, setConfirmRevoke] = useState(false);
    const [revoking, setRevoking] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/auth/session");
                setSession(data);
            } catch {
                // silently keep the block empty — session info is decorative
            } finally { setLoading(false); }
        })();
    }, []);

    const doRevoke = async () => {
        setRevoking(true);
        try {
            await api.post("/auth/revoke-all");
            toast.success("Tüm oturumlar kapatıldı. Yeniden giriş için yönlendiriliyorsun…");
            // Give the toast a beat to render, then hard reload to the login screen.
            setTimeout(() => { window.location.href = "/login"; }, 800);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Oturumlar kapatılamadı");
            setRevoking(false);
        }
    };

    const fmtDate = (iso) => {
        if (!iso) return "—";
        try { return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
        catch { return iso; }
    };

    return (
        <>
            <Section
                title="Güvenlik"
                desc="Hesabına giriş yapmış cihazları yönet ve kimlik doğrulama yöntemini gör."
            >
                <div className="space-y-4" data-testid="security-section">
                    {/* Current session card — "Bu oturum" pill on the right,
                        matching the revoke-all button's design and position for
                        visual symmetry (kullanıcı isteği 2026-02-25). */}
                    <div className="rounded-lg border border-border bg-muted/20 p-4 flex items-start gap-3">
                        <div className="h-9 w-9 rounded-md bg-background border border-border grid place-items-center shrink-0">
                            <Monitor className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium truncate" data-testid="security-current-device">
                                {loading ? <Loader2 className="h-3 w-3 inline animate-spin" /> : (session?.device || "Bilinmeyen cihaz")}
                            </div>
                            <div className="text-[11px] text-muted-foreground truncate mt-0.5" data-testid="security-current-meta">
                                Son giriş tarihi: {fmtDate(session?.last_login_at)}
                                {session?.ip ? ` · IP: ${session.ip}` : ""}
                            </div>
                        </div>
                        <div
                            className="shrink-0 inline-flex items-center h-8 rounded-md border border-success/40 bg-success-soft text-success px-3 text-xs font-medium"
                            data-testid="security-current-pill"
                        >
                            Şu anki oturum.
                        </div>
                    </div>

                    {/* Revoke all — destructive action */}
                    <div className="rounded-lg border border-destructive/20 bg-destructive/5 p-4 flex items-start gap-3">
                        <div className="h-9 w-9 rounded-md bg-destructive/10 text-destructive grid place-items-center shrink-0">
                            <LogOut className="h-4 w-4" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium">Tüm oturumları kapat</div>
                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                                Bu hesaba giriş yapmış <strong>tüm cihazlardaki oturumları</strong> kapatır. Bu cihaz dahil hepsi giriş sayfasına düşer.
                            </div>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            className="border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive shrink-0"
                            onClick={() => setConfirmRevoke(true)}
                            data-testid="security-revoke-all-btn"
                        >
                            Oturumları Kapat
                        </Button>
                    </div>
                </div>
            </Section>

            <AlertDialog open={confirmRevoke} onOpenChange={(o) => !o && setConfirmRevoke(false)}>
                <AlertDialogContent data-testid="security-revoke-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <LogOut className="h-4 w-4 text-destructive" /> Tüm oturumları kapat?
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            Tüm cihazlardaki oturumların kapatılacak — bu cihaz dahil. Yeniden giriş için e-posta adresine OTP göndereceğiz. Devam etmek istiyor musun?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={revoking}>Vazgeç</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={doRevoke}
                            disabled={revoking}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            data-testid="security-revoke-confirm"
                        >
                            {revoking ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Kapatılıyor…</> : "Evet, oturumları kapat"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

